/**
 * Vulnerability detection taxonomy and configuration vocabulary used by the
 * auto-detect classifier (D6 in `docs/auto-detect/plan.md`).
 *
 * The 17 vulnerability types come from the BA-supplied taxonomy aligned with
 * GICOP duty-of-care obligations. The seed migration upserts a row per type
 * into `VulnerabilityType`; these constants exist so that the LLM Zod
 * schema, the per-org config schema, and the cache fingerprint can all
 * agree on the type-key vocabulary without a database lookup.
 *
 * `VULNERABILITY_DETECTION_VERSION` is bumped whenever the prompt body or
 * validation rules change so the semantic-cache fingerprint busts cleanly
 * on deploy.
 */

/**
 * Bumped whenever the vulnerability prompt body, type taxonomy, or
 * validation rules materially change. Mixed into the semantic-cache
 * fingerprint so cached `NO_ISSUE` reuse re-runs the LLM on next deploy.
 */
export const VULNERABILITY_DETECTION_VERSION = 1 as const;

/** Stable category keys (kebab-case). One row in the taxonomy per category. */
export const VULNERABILITY_CATEGORY_KEYS = [
    'health',
    'life-events',
    'financial-hardship',
    'communication-barriers',
    'age-related',
    'cultural-social',
    'systemic-behavioural',
] as const;

export type VulnerabilityCategoryKey = (typeof VULNERABILITY_CATEGORY_KEYS)[number];

export const VULNERABILITY_CATEGORY_LABELS: Readonly<Record<VulnerabilityCategoryKey, string>> = {
    health: 'Health',
    'life-events': 'Life events',
    'financial-hardship': 'Financial hardship',
    'communication-barriers': 'Communication barriers',
    'age-related': 'Age-related',
    'cultural-social': 'Cultural / social',
    'systemic-behavioural': 'Systemic / behavioural flags',
};

/**
 * Stable type keys (kebab-case). The LLM emits one of these strings as
 * `typeKey` on every detection. Items emitted with any other string are
 * dropped server-side rather than failing the message classification —
 * vulnerability is additive, never load-bearing.
 */
export const VULNERABILITY_TYPE_KEYS = [
    'physical-disability',
    'mental-health-condition',
    'cognitive-impairment',
    'bereavement',
    'family-violence-domestic-abuse',
    'severe-personal-stress',
    'temporary-hardship',
    'ongoing-financial-distress',
    'catastrophe-driven-hardship',
    'language-barrier',
    'low-literacy-numeracy',
    'digital-exclusion',
    'elderly-clients',
    'young-inexperienced',
    'cultural-barriers',
    'remote-isolated-clients',
    'third-party-reliance',
] as const;

export type VulnerabilityTypeKey = (typeof VULNERABILITY_TYPE_KEYS)[number];

const VULNERABILITY_TYPE_KEY_SET: ReadonlySet<string> = new Set(VULNERABILITY_TYPE_KEYS);

export function isVulnerabilityTypeKey(value: unknown): value is VulnerabilityTypeKey {
    return typeof value === 'string' && VULNERABILITY_TYPE_KEY_SET.has(value);
}

export const VULNERABILITY_SEVERITY_KEYS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;

export type VulnerabilitySeverityKey = (typeof VULNERABILITY_SEVERITY_KEYS)[number];

/**
 * Authoritative metadata for each type. Mirrors the columns on the
 * `VulnerabilityType` taxonomy table; the seed migration reads these
 * defaults and the runtime reads from the table. Keep in sync with
 * `vulnerability-types-seed.csv`.
 *
 * `defaultExpiryMonths`:
 *   - `null` = no auto-expiry (e.g. a physical disability does not expire);
 *     a sweeper review may still surface "still relevant?" prompts.
 *   - integer = months after which `ContactVulnerabilityFlag` auto-transitions
 *     ACTIVE → EXPIRED. Operator can override per flag at promotion time.
 *
 * `isSensitive: true` types (family violence, severe mental health,
 * bereavement) are gated behind the `message_classification:vulnerability:view_sensitive`
 * permission and require HIGH confidence + a verbatim evidence quote to
 * survive validation.
 */
export type VulnerabilityTypeMetadata = {
    readonly key: VulnerabilityTypeKey;
    readonly label: string;
    readonly categoryKey: VulnerabilityCategoryKey;
    readonly defaultSeverity: VulnerabilitySeverityKey;
    readonly isSensitive: boolean;
    readonly defaultExpiryMonths: number | null;
    readonly sortOrder: number;
};

export const VULNERABILITY_TYPE_METADATA: Readonly<Record<VulnerabilityTypeKey, VulnerabilityTypeMetadata>> = {
    'physical-disability': {
        key: 'physical-disability',
        label: 'Physical disability',
        categoryKey: 'health',
        defaultSeverity: 'MEDIUM',
        isSensitive: false,
        defaultExpiryMonths: null,
        sortOrder: 10,
    },
    'mental-health-condition': {
        key: 'mental-health-condition',
        label: 'Mental health condition',
        categoryKey: 'health',
        defaultSeverity: 'HIGH',
        isSensitive: true,
        defaultExpiryMonths: 12,
        sortOrder: 20,
    },
    'cognitive-impairment': {
        key: 'cognitive-impairment',
        label: 'Cognitive impairment',
        categoryKey: 'health',
        defaultSeverity: 'MEDIUM',
        isSensitive: false,
        defaultExpiryMonths: null,
        sortOrder: 30,
    },
    bereavement: {
        key: 'bereavement',
        label: 'Bereavement',
        categoryKey: 'life-events',
        defaultSeverity: 'HIGH',
        isSensitive: true,
        defaultExpiryMonths: 6,
        sortOrder: 40,
    },
    'family-violence-domestic-abuse': {
        key: 'family-violence-domestic-abuse',
        label: 'Family violence / domestic abuse',
        categoryKey: 'life-events',
        defaultSeverity: 'CRITICAL',
        isSensitive: true,
        defaultExpiryMonths: 12,
        sortOrder: 50,
    },
    'severe-personal-stress': {
        key: 'severe-personal-stress',
        label: 'Severe personal stress',
        categoryKey: 'life-events',
        defaultSeverity: 'HIGH',
        isSensitive: false,
        defaultExpiryMonths: 6,
        sortOrder: 60,
    },
    'temporary-hardship': {
        key: 'temporary-hardship',
        label: 'Temporary hardship',
        categoryKey: 'financial-hardship',
        defaultSeverity: 'MEDIUM',
        isSensitive: false,
        defaultExpiryMonths: 6,
        sortOrder: 70,
    },
    'ongoing-financial-distress': {
        key: 'ongoing-financial-distress',
        label: 'Ongoing financial distress',
        categoryKey: 'financial-hardship',
        defaultSeverity: 'HIGH',
        isSensitive: false,
        defaultExpiryMonths: 12,
        sortOrder: 80,
    },
    'catastrophe-driven-hardship': {
        key: 'catastrophe-driven-hardship',
        label: 'Catastrophe-driven hardship',
        categoryKey: 'financial-hardship',
        defaultSeverity: 'MEDIUM',
        isSensitive: false,
        defaultExpiryMonths: 6,
        sortOrder: 90,
    },
    'language-barrier': {
        key: 'language-barrier',
        label: 'Language barrier',
        categoryKey: 'communication-barriers',
        defaultSeverity: 'MEDIUM',
        isSensitive: false,
        defaultExpiryMonths: null,
        sortOrder: 100,
    },
    'low-literacy-numeracy': {
        key: 'low-literacy-numeracy',
        label: 'Low literacy / numeracy',
        categoryKey: 'communication-barriers',
        defaultSeverity: 'LOW',
        isSensitive: false,
        defaultExpiryMonths: null,
        sortOrder: 110,
    },
    'digital-exclusion': {
        key: 'digital-exclusion',
        label: 'Digital exclusion',
        categoryKey: 'communication-barriers',
        defaultSeverity: 'LOW',
        isSensitive: false,
        defaultExpiryMonths: null,
        sortOrder: 120,
    },
    'elderly-clients': {
        key: 'elderly-clients',
        label: 'Elderly clients',
        categoryKey: 'age-related',
        defaultSeverity: 'MEDIUM',
        isSensitive: false,
        defaultExpiryMonths: null,
        sortOrder: 130,
    },
    'young-inexperienced': {
        key: 'young-inexperienced',
        label: 'Young / inexperienced',
        categoryKey: 'age-related',
        defaultSeverity: 'LOW',
        isSensitive: false,
        defaultExpiryMonths: 12,
        sortOrder: 140,
    },
    'cultural-barriers': {
        key: 'cultural-barriers',
        label: 'Cultural barriers',
        categoryKey: 'cultural-social',
        defaultSeverity: 'LOW',
        isSensitive: false,
        defaultExpiryMonths: null,
        sortOrder: 150,
    },
    'remote-isolated-clients': {
        key: 'remote-isolated-clients',
        label: 'Remote / isolated clients',
        categoryKey: 'cultural-social',
        defaultSeverity: 'LOW',
        isSensitive: false,
        defaultExpiryMonths: 12,
        sortOrder: 160,
    },
    'third-party-reliance': {
        key: 'third-party-reliance',
        label: 'Third-party reliance',
        categoryKey: 'systemic-behavioural',
        defaultSeverity: 'LOW',
        isSensitive: false,
        defaultExpiryMonths: null,
        sortOrder: 170,
    },
};

/**
 * Convenience set of `isSensitive: true` type keys. Used by the result service
 * to apply the higher evidence bar (HIGH confidence + verbatim evidence quote)
 * and by the projection helper to redact for callers without the elevated
 * permission.
 */
export const SENSITIVE_VULNERABILITY_TYPE_KEYS: ReadonlySet<VulnerabilityTypeKey> = new Set(
    (Object.values(VULNERABILITY_TYPE_METADATA) as VulnerabilityTypeMetadata[])
        .filter((m) => m.isSensitive)
        .map((m) => m.key),
);

/**
 * JSON schema for `AUTO_DETECT_VULNERABILITY_DETECTION_ENABLED`.
 *
 * Tri-state boolean: `null` (default) and `true` enable detection; `false`
 * disables it. The tri-state shape is preserved so a future operator-controlled
 * default can be added without a schema migration, but operationally the
 * difference between `null` and `true` is informational only — both run the
 * detector. Set to `false` per-org to opt out entirely.
 */
export const AUTO_DETECT_VULNERABILITY_DETECTION_ENABLED_SCHEMA = {
    type: ['boolean', 'null'],
    default: null,
} as const;

/**
 * JSON schema for `AUTO_DETECT_VULNERABILITY_TYPES_ENABLED`.
 * Optional allowlist of type keys to retain after LLM detection. The model
 * is always shown the full type roster for calibration; disabled keys are
 * stripped post-parse by the result service. `null` (default) = all types
 * enabled per `VulnerabilityType.isEnabled` in the taxonomy table.
 */
export const AUTO_DETECT_VULNERABILITY_TYPES_ENABLED_SCHEMA = {
    type: ['array', 'null'],
    items: { type: 'string', enum: VULNERABILITY_TYPE_KEYS },
    uniqueItems: true,
    default: null,
} as const;

export type AutoDetectVulnerabilityDetectionEnabledValue = boolean | null;
export type AutoDetectVulnerabilityTypesEnabledValue = VulnerabilityTypeKey[] | null;
